'use client';
import { CSSProperties, ReactNode } from 'react';
import { ErrorBoundary } from '@/shared/ui/error-boundary';

type Props = {
    children: ReactNode;
    /** Prefab or component name, used for the Sentry tag and the preview placeholder. */
    label?: string;
    /** In preview/editor mode show a visible placeholder; in the public eshop skip silently. */
    preview?: boolean;
};

const placeholder: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '64px',
    padding: '16px',
    margin: '8px 0',
    border: '1px dashed rgba(0, 0, 0, 0.3)',
    borderRadius: '8px',
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: '14px',
    textAlign: 'center',
};

/**
 * Isolates a single page section so a crash in one prefab/component does not blank the
 * whole page. Public eshop favors graceful degradation (skip the broken section); the
 * editor favors visibility (show which prefab failed, and don't spam Sentry — the admin
 * sees it live).
 */
export const PrefabErrorBoundary = ({ children, label, preview }: Props) => (
    <ErrorBoundary
        sentryTags={{ area: 'prefab', prefab: label ?? 'unknown' }}
        skipSentry={preview}
        fallback={() =>
            preview ? (
                <div style={placeholder} role="alert">
                    Failed to render the “{label ?? 'unknown'}” component
                </div>
            ) : null
        }
    >
        {children}
    </ErrorBoundary>
);
